import React from "react";
import { defineMessage, useIntl } from "react-intl";
import ColorLegend from "../charts/lib/ColorLegend";
import { Line as FixedWidthLineChart } from "../charts/lib/Lines";
import { ChartGrid } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_quality_fig_TRAINED";
import { datasets } from "../data/data_quality_fig_TRAINED_datasets";
import { countryIdIso, foldGeoEntity, getCountryName, mkGemEntityDecoder, useCountryEntities, useFigureControlItems, useFigureState, useMultiSelectState, useNamedEntities } from "../domain";
import { useMedia, useOrderedColorScale } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { O, pipe } from "../prelude";
export * from "../data/data_quality_fig_TRAINED";
const DEFAULT_COUNTRIES = [
    "RWA",
    "LSO",
    "GHA",
    "ERI",
    "SEN",
    "MOZ"
];
const yUnitLabel = defineMessage({
    id: "fig.quality.TRAINED.legend",
    defaultMessage: "Percentage of trained teachers"
});
export const Data = mkGemEntityDecoder([
    "ind_id",
    "level",
    "year",
    "value"
], {});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedCountries] = useMultiSelectState("countries");
    const entityData_ = useNamedEntities(data);
    const entityData = React.useMemo(()=>{
        return entityData_.filter((x)=>x.level === "primary");
    }, [
        entityData_
    ]);
    const countryData = useCountryEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            selected: O.some(DEFAULT_COUNTRIES),
            maxItems: 8
        }, countryControlItems);
    }, [
        intl,
        actions,
        countryControlItems
    ]);
    const chartData = React.useMemo(()=>{
        const hasCountry = (country)=>pipe(selectedCountries, O.fold(()=>false, (xs)=>xs.includes(countryIdIso.unwrap(country))));
        return unsafeFromArray(entityData.filter(foldGeoEntity({
            country: ({ id })=>hasCountry(id),
            region: ()=>true
        })));
    }, [
        entityData,
        selectedCountries
    ]);
    const selectedCountryLabels = React.useMemo(()=>{
        return pipe(selectedCountries, O.map((xs)=>xs.map((x)=>getCountryName(env.countries, countryIdIso.wrap(x)))));
    }, [
        env.countries,
        selectedCountries
    ]);
    const { colorScale, colorLegendValues } = useOrderedColorScale(selectedCountryLabels);
    return <ChartGrid data={chartData} getCell={React.useCallback((x)=>x.entity_type === "country" ? intl.formatMessage(defineMessage({
            id: "Countries",
            defaultMessage: "Countries"
        })) : intl.formatMessage(defineMessage({
            id: "Regions",
            defaultMessage: "Regions"
        })), [
        intl
    ])} columnCount={2} minWidth={100}>
      {({ data, width, first })=>{
        return <>
            <FixedWidthLineChart tLabel={(s)=>s} width={width} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} endLabel={!screenMDown} x="year" timeParse="%Y" timeFormat="%Y" numberFormat=".0%" yTicks={[
            0,
            0.2,
            0.4,
            0.6,
            0.8,
            1
        ]} category="datum.entity_name" colorLegend={!first} colorScale={first ? colorScale : undefined} values={data} markStyle={(x)=>x.entity_type === "world" ? "reference" : "normal"} connectLines showTooltip yAxisLabel={intl.formatMessage(yUnitLabel)} yAxisLabelPadding={24}/>
            {first && <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>}
          </>;
    }}
    </ChartGrid>;
};
export default withFigureIO({
    url: new URL("../data/data_quality_fig_TRAINED.json", import.meta.url).href,
    csv: new URL("../data/data_quality_fig_TRAINED.zip", import.meta.url).href,
    xlsx: new URL("../data/data_quality_fig_TRAINED.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
